import { useState } from 'react';
import { toast } from 'sonner';
import { Mail, Download, X, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

// ─── Props ────────────────────────────────────────────────────────────────────

interface EmailPdfModalProps {
  open: boolean;
  onClose: () => void;
  endpoint: string;
  defaultTo: string;
  defaultSubject: string;
  pdfHref: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function EmailPdfModal({
  open,
  onClose,
  endpoint,
  defaultTo,
  defaultSubject,
  pdfHref,
}: EmailPdfModalProps) {
  const [to, setTo] = useState(defaultTo);
  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [smtpError, setSmtpError] = useState(false);

  if (!open) return null;

  const downloadHref = pdfHref.includes('?')
    ? `${pdfHref}&download=1`
    : `${pdfHref}?download=1`;

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    if (!to.trim()) {
      toast.error('Please enter a recipient email address');
      return;
    }
    setSending(true);
    setSmtpError(false);
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: to.trim(),
          subject: subject.trim(),
          message: message.trim() || undefined,
        }),
      });

      const body = await res.json().catch(() => ({})) as {
        message?: string;
        error?: string;
        fallback?: string;
      };

      if (res.status === 401) {
        window.location.href = '/login';
        return;
      }

      if (res.status === 409) {
        // SMTP not configured — show fallback download notice
        setSmtpError(true);
        return;
      }

      if (!res.ok) {
        toast.error(body.error ?? `Failed (HTTP ${res.status})`);
        return;
      }

      toast.success(body.message ?? 'Email sent successfully');
      onClose();
    } catch {
      toast.error('Network error — could not send email');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-background shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-base font-semibold">Email PDF</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* SMTP not configured fallback */}
        {smtpError ? (
          <div className="p-6 space-y-4">
            <div className="rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30 px-4 py-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                <div className="space-y-1">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                    Email not configured
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    SMTP settings haven't been set up yet. Download the PDF instead and send it
                    manually.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <a href={downloadHref} download>
                <Button>
                  <Download className="h-3.5 w-3.5 mr-1.5" />
                  Download PDF
                </Button>
              </a>
            </div>
          </div>
        ) : (
          /* Normal email form */
          <form onSubmit={handleSend} className="p-6 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email-to">
                To <span className="text-destructive">*</span>
              </Label>
              <Input
                id="email-to"
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="customer@example.com"
                required
                disabled={sending}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email-subject">
                Subject <span className="text-destructive">*</span>
              </Label>
              <Input
                id="email-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
                disabled={sending}
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="email-message">Message (optional)</Label>
              <textarea
                id="email-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Optional message…"
                rows={4}
                disabled={sending}
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
              />
            </div>

            <p className="text-xs text-muted-foreground">
              The PDF will be attached automatically.
            </p>

            <div className="flex items-center justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={onClose} disabled={sending}>
                Cancel
              </Button>
              <Button type="submit" disabled={sending}>
                <Mail className="h-3.5 w-3.5 mr-1.5" />
                {sending ? 'Sending…' : 'Send Email'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
